// importation of libraries
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";

import { unFormatNumberWithCommas } from "@/utils/formatNumberWithCommas";
import { useTranslation } from "react-i18next";
import useStore from "store";
import * as yup from "yup";
import AmountInput from "../ui/AmountInput";
import { Button } from "../ui/Button";

// schema options
const schema = yup
  .object()
  .shape({
    amount: yup
      .string()
      .required("dialogs.auto_recharge.validation.amount.required")
      .min(3, "dialogs.auto_recharge.validation.amount.min")
      .max(7, "dialogs.auto_recharge.validation.amount.max"),
  })
  .required();

export default function TransferAutoCharge() {
  const { t } = useTranslation("dashboard");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  // const getUser = useStore((state) => state.getUser);
  const currentUser = useStore(state => state.currentUser);
  const transferChargeAuto = useStore(state => state.transferChargeAuto);
  const getCurrentUser = useStore(state => state.getCurrentUser);
  const getAllTransactions = useStore(state => state.getAllTransactions);

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: yupResolver(schema) });

  // variables
  const minCharge = currentUser?.account?.minCharge;
  const maxCharge = currentUser?.account?.maxCharge;
  const maxChargeAmount = currentUser?.account?.maxChargeAmount;
  const remainingCharge = currentUser?.account?.remainingCharge;

  const isAmountAvailable = amount =>
    amount <= maxCharge &&
    amount <= maxChargeAmount &&
    amount <= remainingCharge;

  // Create an array of suggested amounts based on minCharge and remaining balance
  const amountSuggestions = [
    isAmountAvailable(minCharge) ? minCharge : null,
    isAmountAvailable(minCharge * 2) ? minCharge * 2 : null,
    isAmountAvailable(minCharge * 3) ? minCharge * 3 : null,
    isAmountAvailable(maxCharge) ? maxCharge : null,
  ]
    .filter(Boolean)
    // remove repeated values, if they exists
    .filter((value, index, self) => self.indexOf(value) === index);

  const handleClickOpenButton = e => {
    onOpen();
    e.stopPropagation();
  };

  // submit function
  async function onSubmit(values, e) {
    e.preventDefault();
    const charge = {
      amount: unFormatNumberWithCommas(values.amount),
    };

    // respond
    const res = await transferChargeAuto(charge);

    if (res?.status === 201) {
      toast({
        isClosable: true,
        title: t("successful", { ns: "common" }),
        status: "success",
        duration: 3000,
      });
      await getCurrentUser();
      await getAllTransactions(true, null, { size: 10, page: 0 });

      onClose();
      reset();
    } else {
      toast({
        isClosable: true,
        title:
          res?.data?.message || t("errors.something_wrong", { ns: "common" }),
        status: "error",
        duration: 3000,
      });
    }
  }

  return (
    <>
      <Button onClick={handleClickOpenButton} size="sm" className="w-fit">
        {t("dialogs.auto_recharge.title")}
      </Button>

      <Modal size="md" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("dialogs.auto_recharge.title")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={4}>
                <Flex flexDirection="column">
                  <FormLabel>
                    {t("dialogs.auto_recharge.min_amount")}
                    <Box as="span" color={"green"}>
                      &nbsp; {minCharge.toLocaleString()} {t("DA")}
                    </Box>
                  </FormLabel>
                  <FormLabel>
                    {t("dialogs.auto_recharge.max_amount")}

                    <Box as="span" color={"green"}>
                      &nbsp; {maxCharge.toLocaleString()} {t("DA")}
                    </Box>
                  </FormLabel>

                  <FormLabel>
                    {t("dialogs.auto_recharge.allowed_balance")}
                    <Box as="span" color={"green"}>
                      &nbsp; {maxChargeAmount.toLocaleString()} {t("DA")}
                    </Box>
                  </FormLabel>

                  <FormLabel>
                    {t("dialogs.auto_recharge.remaining_balance")}
                    <Box as="span" color={"green"}>
                      &nbsp; {remainingCharge.toLocaleString()} {t("DA")}
                    </Box>
                  </FormLabel>
                </Flex>
                <div className="flex flex-col gap-2">
                  <FormControl id="email" isInvalid={errors.amount}>
                    <AmountInput
                      register={register("amount")}
                      errorMessage={t(errors.amount?.message)}
                      setValue={setValue}
                      containerClassName="mt-0"
                    />
                  </FormControl>

                  <div className="flex gap-2 flex-wrap">
                    {amountSuggestions.map((suggestion, index) => {
                      if (suggestion) {
                        return (
                          <Button
                            key={index}
                            variant="outline"
                            type="button"
                            size="sm"
                            onClick={() => setValue("amount", suggestion)}
                          >
                            {suggestion.toLocaleString()} {t("DA")}
                          </Button>
                        );
                      }
                    })}
                  </div>
                </div>
              </Stack>
              <Flex my="8" justify="flex-end" gap={2}>
                <Button
                  type="button"
                  variant="outline"
                  mr={3}
                  onClick={onClose}
                >
                  {t("dialogs.auto_recharge.cancel")}
                </Button>
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting}
                  colorScheme="green"
                >
                  {t("dialogs.auto_recharge.confirm")}
                </Button>
              </Flex>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
